import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Bot, User, Loader2 } from 'lucide-react';

const FloatingWidgets = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  
  const SYSTEM_PROMPT = `You are a helpful, professional, and friendly customer support assistant for Vishwa DigiTech, a top-tier IT and Digital Marketing company in India. 
Your goal is to assist users with their queries about our services (Web Development, App Development, Custom Software, Digital Marketing, SEO, Social Media, WhatsApp APIs, Video Production, Graphic Design).
If a user asks about pricing, timelines, or complex project details, politely inform them that you need our experts to evaluate their requirements and ask for their Name and Phone Number. 
If they provide their details, thank them and say an expert will contact them shortly.
Always keep responses concise (1-3 short sentences) and format them beautifully. Don't use markdown headers, just plain text with occasional bolding.`;

  // Load initial greeting message on mount
  useEffect(() => {
    setMessages([
      { text: "Hello! I am the Vishwa DigiTech AI Assistant. How can I help you grow your business today?", isBot: true }
    ]);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, isChatOpen]);

  const getAIResponse = async (userText, currentMessages) => {
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    if (!apiKey) {
      return "ERROR: Missing API key in .env file.";
    }

    try {
      const formattedHistory = currentMessages
        .filter(m => m.text !== "Hello! I am the Vishwa DigiTech AI Assistant. How can I help you grow your business today?") 
        .map(msg => ({
          role: msg.isBot ? "model" : "user",
          parts: [{ text: msg.text }],
        }));

      // Add system prompt context to the user's current message
      const contextMessage = `System Context: ${SYSTEM_PROMPT}\n\nUser Message: ${userText}`;
      formattedHistory.push({ role: "user", parts: [{ text: contextMessage }] });

      // Using the correct model available for this API key
      const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-flash-latest:generateContent?key=${apiKey}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contents: formattedHistory })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
      }

      const data = await response.json();
      return data.candidates[0].content.parts[0].text;
    } catch (error) {
      console.error("AI Chat Error:", error);
      return `GOOGLE API ERROR: ${error.message}`;
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userMessage = inputValue;
    const currentMessages = [...messages];
    
    // Add user message
    const newMessages = [...currentMessages, { text: userMessage, isBot: false }];
    setMessages(newMessages);
    setInputValue('');
    setIsTyping(true);

    // Get AI Response
    const aiText = await getAIResponse(userMessage, currentMessages);
    
    setIsTyping(false);
    setMessages(prev => [...prev, { text: aiText, isBot: true }]);
  };

  const whatsappNumber = "919699938509";
  const whatsappUrl = `https://wa.me/${whatsappNumber}`;

  return (
    <div className="fixed bottom-6 right-6 z-[100] flex flex-col items-end gap-4" style={{ fontFamily: 'emoji' }}>
      
      {/* Chatbot Window */}
      {isChatOpen && (
        <div className="w-[340px] sm:w-[400px] h-[500px] max-h-[80vh] bg-[#0F1629] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300 origin-bottom-right mb-2">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#0EA5E9] to-[#0B2B5E] p-4 flex justify-between items-center shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center relative">
                <Bot className="w-6 h-6 text-white" />
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-[#0B2B5E] rounded-full"></span>
              </div>
              <div>
                <h4 className="text-white font-bold text-sm">Vishwa AI Assistant</h4>
                <p className="text-white/70 text-xs flex items-center gap-1">
                  Always here to help
                </p>
              </div>
            </div>
            <button onClick={() => setIsChatOpen(false)} className="text-white/80 hover:text-white transition-colors bg-white/10 p-1.5 rounded-lg hover:bg-white/20">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Chat Messages */}
          <div ref={chatContainerRef} className="flex-1 p-4 overflow-y-auto bg-[#0A0F1F] space-y-4">
            {messages.map((msg, index) => (
              <div key={index} className={`flex ${msg.isBot ? 'justify-start' : 'justify-end'}`}>
                {msg.isBot && (
                   <div className="w-8 h-8 rounded-full bg-[#0EA5E9]/20 flex items-center justify-center mr-2 shrink-0 self-end mb-1">
                      <Bot className="w-4 h-4 text-[#0EA5E9]" />
                   </div>
                )}
                <div className={`max-w-[75%] p-3 text-sm shadow-md leading-relaxed ${msg.isBot ? 'bg-[#1E293B] text-gray-200 rounded-2xl rounded-bl-sm border border-white/5' : 'bg-gradient-to-br from-[#0EA5E9] to-blue-600 text-white rounded-2xl rounded-br-sm'}`}>
                  {msg.text.replace(/\*\*/g, '')} {/* Basic markdown bold cleanup */}
                </div>
              </div>
            ))}
            
            {/* Typing Indicator */}
            {isTyping && (
               <div className="flex justify-start">
                  <div className="w-8 h-8 rounded-full bg-[#0EA5E9]/20 flex items-center justify-center mr-2 shrink-0 self-end mb-1">
                      <Bot className="w-4 h-4 text-[#0EA5E9]" />
                  </div>
                  <div className="bg-[#1E293B] border border-white/5 p-4 rounded-2xl rounded-bl-sm flex items-center gap-1.5 shadow-md">
                     <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                     <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                     <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
               </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <form onSubmit={handleSendMessage} className="p-3 bg-[#0F1629] border-t border-white/10 flex gap-2 shrink-0">
            <input 
              type="text" 
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Ask me anything..." 
              className="flex-1 bg-[#1E293B] border border-white/10 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-[#0EA5E9] transition-colors"
              disabled={isTyping}
            />
            <button 
              type="submit" 
              disabled={!inputValue.trim() || isTyping}
              className="w-10 h-10 rounded-xl bg-[#0EA5E9] flex items-center justify-center text-white hover:bg-[#0284C7] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
            >
              {isTyping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col gap-3">
        {/* Chat Button */}
        <button 
          onClick={() => setIsChatOpen(!isChatOpen)}
          className="w-14 h-14 bg-gradient-to-tr from-violet-500 to-[#0EA5E9] rounded-full shadow-[0_0_20px_rgba(14,165,233,0.3)] flex items-center justify-center text-white hover:scale-110 transition-transform duration-300 group relative"
          title="Chat with Vishwa AI"
        >
          {isChatOpen ? <X className="w-6 h-6" /> : <Bot className="w-6 h-6" />}
          
          {/* Notification Dot */}
          {!isChatOpen && (
            <span className="absolute top-0 right-0 flex h-4 w-4">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 border-2 border-[#061225]"></span>
            </span>
          )}
        </button>

        {/* WhatsApp Button */}
        <a 
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 h-14 bg-[#25D366] rounded-full shadow-[0_0_20px_rgba(37,211,102,0.3)] flex items-center justify-center text-white hover:scale-110 transition-transform duration-300 group"
          title="WhatsApp Us"
        >
          <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.802.372-.282.297-1.079 1.054-1.079 2.568 0 1.514 1.104 2.978 1.258 3.177.154.198 2.164 3.303 5.241 4.632.732.316 1.303.504 1.748.646.735.233 1.403.2 1.93.121.58-.087 1.758-.718 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </a>
      </div>
    </div>
  );
};

export default FloatingWidgets;
